import { useState } from "react";
import { Football, GoogleLogo, ArrowRight, Check, ShieldCheck, ChartBar, Lightning, Trophy, Crosshair, ArrowsLeftRight, SignOut } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, trackEvent } from "@/App";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function SleeperLogin({ user, onSleeperLinked, onGuestSleeper }) {
  const [username, setUsername] = useState(() => localStorage.getItem("dtm_prefill_sleeper") || "");
  const [loading, setLoading] = useState(false);
  const [sleeperData, setSleeperData] = useState(null);
  const [discoveredLeagues, setDiscoveredLeagues] = useState([]);
  const [selectedLeagues, setSelectedLeagues] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const isLoggedIn = user && user.email;

  const handleGoogleLogin = () => {
    window.location.href = "/api/auth/google/login";
  };

  const handleSleeperLookup = async () => {
    if (!username.trim()) { toast.error("Enter your Sleeper username"); return; }
    setLoading(true);
    try {
      if (isLoggedIn) {
        const res = await axios.post(`${API}/auth/link-sleeper`, { username: username.trim() });
        setSleeperData(res.data);
      } else {
        const res = await axios.get(`https://api.sleeper.app/v1/user/${username.trim()}`);
        if (!res.data || !res.data.user_id) throw new Error("Not found");
        setSleeperData({
          sleeper_user_id: res.data.user_id,
          sleeper_username: username.trim(),
          display_name: res.data.display_name || username.trim(),
          avatar: res.data.avatar,
        });
      }

      const uid = isLoggedIn ? (await axios.post(`${API}/auth/link-sleeper`, { username: username.trim() })).data.sleeper_user_id : (await axios.get(`https://api.sleeper.app/v1/user/${username.trim()}`)).data.user_id;
      const leaguesRes = await axios.get(`${API}/auth/leagues/${uid}`);
      setDiscoveredLeagues(leaguesRes.data);
      setSelectedLeagues(new Set(leaguesRes.data.map(l => l.league_id)));
      toast.success(`Found ${leaguesRes.data.length} leagues!`);
      trackEvent("sleeper_import", `${leaguesRes.data.length} leagues`);
    } catch (e) {
      toast.error("Could not find that username on Sleeper");
    } finally {
      setLoading(false);
    }
  };

  const toggleLeague = (lid) => {
    setSelectedLeagues(prev => {
      const next = new Set(prev);
      if (next.has(lid)) next.delete(lid); else next.add(lid);
      return next;
    });
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const userId = user?.user_id || `guest_${sleeperData?.sleeper_user_id || ""}`;
      for (const lid of selectedLeagues) {
        const league = discoveredLeagues.find(l => l.league_id === lid);
        try {
          await axios.post(`${API}/leagues`, { league_id: lid, user_id: userId });
          if (league?.my_roster_id) {
            await axios.put(`${API}/leagues/${lid}/my-team`, { my_roster_id: league.my_roster_id });
          }
        } catch (e) { /* may already exist */ }
      }
      if (isLoggedIn) { onSleeperLinked(user); }
      else { onGuestSleeper(sleeperData); }
    } catch (e) {
      toast.error("Failed to import leagues");
    } finally {
      setImporting(false);
    }
  };

  // Step 1: Connect Sleeper
  if (!sleeperData && discoveredLeagues.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-12 h-12 bg-[#ccff00] flex items-center justify-center rounded-sm mx-auto mb-4">
              <Football size={24} weight="bold" color="#000" />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white">
              CONNECT YOUR <span className="text-[#ccff00]">SLEEPER LEAGUE</span>
            </h1>
            <p className="text-sm text-[#a3a3a3] mt-2 max-w-md mx-auto">
              Link your Sleeper account to unlock AI trade analysis, contender rankings, and tools built around your actual roster.
            </p>
          </div>

          {/* Main card: username input */}
          <div className="border border-[#262626] bg-[#171717] p-5 sm:p-6 mb-5" data-testid="sleeper-entry-form">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck size={16} weight="bold" className="text-[#ccff00]" />
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ccff00]">READ-ONLY ACCESS — NO PASSWORD NEEDED</p>
            </div>
            <div className="flex gap-3">
              <Input
                placeholder="Your Sleeper username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSleeperLookup()}
                className="h-12 bg-[#0a0a0a] border-[#262626] text-white text-base font-mono placeholder:text-[#444] focus:ring-2 focus:ring-[#ccff00]"
                data-testid="sleeper-username-input"
              />
              <Button onClick={handleSleeperLookup} disabled={loading} className="h-12 px-6 btn-primary rounded-sm shrink-0" data-testid="explore-btn">
                {loading ? <div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />Finding...</div> : <>CONNECT <ArrowRight size={14} weight="bold" className="ml-1" /></>}
              </Button>
            </div>
            <p className="text-[10px] text-[#666] mt-2">Open the Sleeper app → Settings → your username is at the top. We only read your league data — we never post, message, or modify anything.</p>
          </div>

          {/* What happens when you connect */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            {[
              { icon: <ChartBar size={16} weight="bold" />, title: "See your roster valued", desc: "Every player on your team priced with live FantasyCalc values — Superflex and 1QB." },
              { icon: <Lightning size={16} weight="bold" />, title: "Get AI trade packages", desc: "Trades built for YOUR roster, balanced within 8%, with explanations for both sides." },
              { icon: <Trophy size={16} weight="bold" />, title: "Grade vs. champions", desc: "Your roster scored against 6,976 real championship teams. See where you stack up." },
            ].map(item => (
              <div key={item.title} className="border border-[#262626] bg-[#171717] p-4">
                <div className="text-[#ccff00] mb-2">{item.icon}</div>
                <p className="text-xs font-bold text-white mb-1">{item.title}</p>
                <p className="text-[10px] text-[#888] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Additional tools preview */}
          <div className="border border-[#262626] bg-[#171717] p-4 mb-5">
            <p className="text-[10px] font-bold uppercase text-[#555] mb-3">ALSO UNLOCKED</p>
            <div className="flex flex-wrap gap-2">
              {[
                { icon: <Crosshair size={10} />, label: "Contender Rankings" },
                { icon: <ArrowsLeftRight size={10} />, label: "Trade Finder" },
                { icon: <ChartBar size={10} />, label: "Trade Simulator" },
                { icon: <Lightning size={10} />, label: "Roster Optimizer" },
                { icon: <Trophy size={10} />, label: "Trade Partner Matching" },
              ].map(t => (
                <span key={t.label} className="inline-flex items-center gap-1 text-[10px] text-[#888] bg-[#0a0a0a] border border-[#262626] px-2 py-1 rounded-sm">
                  <span className="text-[#ccff00]">{t.icon}</span> {t.label}
                </span>
              ))}
            </div>
          </div>

          {/* Google login if not logged in */}
          {!isLoggedIn && (
            <div className="text-center">
              <p className="text-[10px] text-[#666] mb-2">Already have an account?</p>
              <button onClick={handleGoogleLogin} className="inline-flex items-center gap-2 px-5 py-2 bg-white text-black font-bold text-xs rounded-sm hover:bg-gray-100 transition-colors" data-testid="sleeper-google-login-btn">
                <GoogleLogo size={14} weight="bold" /> Sign in with Google
              </button>
            </div>
          )}

          {isLoggedIn && (
            <div className="text-center space-y-3">
              <div className="text-xs text-[#4ade80] flex items-center justify-center gap-1">
                <Check size={14} weight="bold" />
                Signed in as {user.email}
              </div>
              <div className="flex items-center justify-center gap-4">
                <button onClick={() => { localStorage.removeItem("dynastydeal_user"); localStorage.removeItem("dynastydeal_session_token"); window.location.href = "/"; }} className="text-[10px] text-[#888] hover:text-white transition-colors underline" data-testid="sleeper-signout-btn">
                  Sign out
                </button>
                <span className="text-[#333]">·</span>
                <Link to="/trade-calculator" className="text-[10px] text-[#888] hover:text-[#ccff00] transition-colors no-underline">
                  Skip — use free tools
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Step 2: League discovery
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <h2 className="font-heading text-2xl text-white uppercase">
            {sleeperData?.display_name || username}&apos;s LEAGUES
          </h2>
          <p className="text-sm text-[#a3a3a3] mt-1">{discoveredLeagues.length} leagues found · Select the ones to import</p>
        </div>

        <div className="border border-[#262626] bg-[#171717] mb-4" data-testid="league-discovery">
          <ScrollArea className="max-h-[400px]">
            {discoveredLeagues.map((league) => {
              const isSelected = selectedLeagues.has(league.league_id);
              return (
                <button
                  key={league.league_id}
                  onClick={() => toggleLeague(league.league_id)}
                  className={`w-full flex items-center justify-between px-4 py-3 border-b border-[#1a1a1a] text-left transition-colors ${isSelected ? "bg-[#ccff00]/5" : "hover:bg-[#262626]"}`}
                  data-testid={`league-select-${league.league_id}`}
                >
                  <div>
                    <p className="text-sm font-semibold text-white">{league.name}</p>
                    <p className="text-[10px] font-mono text-[#666]">{league.scoring_type} · {league.total_rosters} teams</p>
                  </div>
                  <div className={`w-5 h-5 border-2 rounded-sm flex items-center justify-center ${isSelected ? "border-[#ccff00] bg-[#ccff00]" : "border-[#666]"}`}>
                    {isSelected && <Check size={12} weight="bold" color="#000" />}
                  </div>
                </button>
              );
            })}
          </ScrollArea>
        </div>

        <Button onClick={handleImport} disabled={importing || selectedLeagues.size === 0} className="w-full h-12 btn-primary rounded-sm" data-testid="import-leagues-btn">
          {importing ? <div className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />IMPORTING...</div> : <><ArrowRight size={18} weight="bold" className="mr-2" />IMPORT {selectedLeagues.size} LEAGUE{selectedLeagues.size !== 1 ? "S" : ""}</>}
        </Button>

        <p className="text-center text-[10px] text-[#555] mt-3">Your league data is read-only. We never post, trade, or modify anything on Sleeper.</p>
      </div>
    </div>
  );
}
